import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui-kit/card';
import { Activity, Clock, DollarSign, FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface Stats {
  totalRequests: number;
  totalTokens: number;
  totalCost: number;
  avgResponseTime: number;
  successRate: number;
}

interface StatsCardsProps {
  stats: Stats;
  loading?: boolean;
}

export function StatsCards({ stats, loading }: StatsCardsProps) {
  const { t } = useTranslation();
  if (loading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {[...Array(4)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div className="h-4 w-24 bg-muted rounded" />
            </CardHeader>
            <CardContent>
              <div className="h-8 w-16 bg-muted rounded" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">
            {t('ADMIN_ANALYTICS_STATS_TOTALREQUESTS')}
          </CardTitle>
          <Activity className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{stats.totalRequests.toLocaleString()}</div>
          <p className="text-xs text-muted-foreground">
            {t('ADMIN_ANALYTICS_STATS_TOTALREQUESTS_DESC')}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">
            {t('ADMIN_ANALYTICS_STATS_TOTALCOST')}
          </CardTitle>
          <DollarSign className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">${stats.totalCost.toFixed(4)}</div>
          <p className="text-xs text-muted-foreground">
            {t('ADMIN_ANALYTICS_STATS_TOTALCOST_DESC')}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">
            {t('ADMIN_ANALYTICS_STATS_TOTALTOKENS')}
          </CardTitle>
          <FileText className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{(stats.totalTokens / 1000).toFixed(1)}k</div>
          <p className="text-xs text-muted-foreground">
            {t('ADMIN_ANALYTICS_STATS_TOTALTOKENS_DESC')}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">
            {t('ADMIN_ANALYTICS_STATS_LATENCY')}
          </CardTitle>
          <Clock className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{stats.avgResponseTime.toFixed(3)}ms</div>
          <p className="text-xs text-muted-foreground">{t('ADMIN_ANALYTICS_STATS_LATENCY_DESC')}</p>
        </CardContent>
      </Card>
    </div>
  );
}
